<template>
  <div>
    <div id="3d-graph" @click="handleClick"></div>
    <div 
      class="book-container" 
      id="book-container" 
      :class="{ 'collapsed': isBookContainerCollapsed }"
      :style="{ zIndex: bookContainerZIndex }" 
      @click="bringBookContainerToFront"
      v-show="isShowInfo"
    >
      <!-- 收起/展开按钮 -->
      <div class="collapse-toggle" @click.stop="toggleBookContainer">
        <i :class="isBookContainerCollapsed ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
        <span>{{ isBookContainerCollapsed ? '展开' : '收起' }}</span>
      </div>
      
      <!-- 信息内容区域 -->
      <div class="book-content">
        <h3 v-if="lineTitle" class="keyword-title">{{ lineTitle.title }}</h3>
        <div v-if="currentBondList.length > 0" class="related-nodes-count">
          <span>相关联节点: {{ currentBondList.length }}个</span>
        </div>
        <book
          :isShowInfo="isShowInfo"
          :currentBookId="currentBookId"
          :currentKeyword="currentKeyword"
          :currentNodeId="currentNodeId"
          :forceData="forceData"
          :bondList="bondList"
          @showBookDetail="showBookDetail"
          @addRelatedKeyword="handleAddRelatedKeyword"
        />
        <book
          v-for="item in currentBondList"
          :key="item.uniqueKey || item?.book_keywords_id || item.member_keywords_id"
          :isShowInfo="isShowInfo"
          :currentBookId="item.book_keywords_id"
          :currentKeyword="item.keywords"
          :currentNodeId="item.member_keywords_id"
          :forceData="forceData"
          :bondList="bondList"
          @showBookDetail="showBookDetail"
        />
      </div>
    </div>
    <BookDetail :book_id="choosedBookId" :key="choosedBookId" :style="{ zIndex: bookDetailZIndex }" @bring-to-front="bringBookDetailToFront" />
    <BookDetailsPopup :style="{ zIndex: bookDetailsPopupZIndex }" @bring-to-front="bringBookDetailsPopupToFront" />
    <!-- <MindMap /> -->
    <div class="node-active" v-show="activeLabel.text" :style="getStyle">
      <p>{{activeLabel.text}}</p>
    </div>
  </div>
</template>

<script lang="js">
import * as THREE from 'three'
import ForceGraph3D from '3d-force-graph'
import { getBondNode, getBondTitle } from './utils'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass'
import book from './components/book/index.vue'
// import MindMap from '@/components/mind-map/index.vue'
import BookDetailsPopup from '@/components/nebula-sub/book-details-popup.vue'
import BookDetail from '@/components/nebula-sub/book-details.vue'
import { EventBus } from '@/main.js'
import { mapState, mapMutations } from 'vuex'
import request from '@/http/request'

let Graph = null
let isRotationActive = true

let isDragging = false // 是否正在拖动
let startX = 0 // 鼠标按下时的X坐标
let scrollLeft = 0 // 当前滚动的距离
let bloomPass = null

const dashedMaterial = new THREE.LineDashedMaterial({
  color: '#fff',
  dashSize: 1,
  gapSize: 2,
  linewidth: 5,
  scale: 1
})
export default {
  data: function () {
    return {
      activeLabel: {
        text: '',
        x: 0,
        y: 0
      },
      lineTitle: null,
      isShowInfo: false,
      currentKeyword: '',
      currentBookId: '',
      currentNodeId: '',
      currentBondList: [], // 当前关联列表
      updateThrottle: null, // 节流更新定时器
      keywordsConnections: [], // 存储关键词之间的连接关系
      // z-index 管理 - 使用递增策略
      bookContainerZIndex: 1000, // 星点/星链信息的z-index
      bookDetailZIndex: 999, // 书籍详情的z-index
      bookDetailsPopupZIndex: 998, // 书籍详情弹窗的z-index
      currentMaxZIndex: 1002, // 当前最大的z-index值，每次点击递增
      isBookContainerCollapsed: false, // 控制信息框收起状态
      isGraphInitialized: false, // 标记图形是否已初始化
      hasInitialData: false // 标记是否已有初始数据
    }
  },
  computed: {
    ...mapState('book', ['choosedBookId', 'isShowBookDetail']),
    ...mapState('indexsearch', {
      choosedSearchItem: (state) => state.chooseSearchItem
    }),
    getStyle () {
      return {
        left: `${this.activeLabel.x}px`,
        top: `${this.activeLabel.y}px`
      }
    }
  },
  props: ['forceData', 'bondList'],
  watch: {
    choosedBookId (newVal) {
      // 在这里处理 choosedBookId 的变化
      this.setShowDetails()
      // 当有书籍被选中时，将书籍详情置于顶层
      if (newVal) {
        this.$nextTick(() => {
          this.bringBookDetailToFront()
        })
      }
    },
    isShowBookDetail (newVal) {
      if (newVal === 'false') {
        // 解除锁定
        this.isLocked = false
      }
    },
    choosedSearchItem (value) {
      this.isLocked = false
      this.chooseSearchItem(value)
    },
    // 监听forceData变化，支持SSE流式数据更新
    forceData: {
      handler (newVal, oldVal) {
        // 如果图形还未初始化，跳过处理（让 init() 方法处理初始数据）
        if (!this.isGraphInitialized) {
          console.log('【动效优化】图形未初始化，跳过数据更新')
          return
        }
        
        // 记录节点数量变化，但允许所有更新直到达到1000个节点
        if (Graph && Graph.graphData && Graph.graphData().nodes) {
          const currentNodeCount = Graph.graphData().nodes.length
          const newNodeCount = newVal.nodes ? newVal.nodes.length : 0
          console.log(`【动效优化】节点数量变化：${currentNodeCount} → ${newNodeCount}`)
          
          // 只有在当前已经有1000个节点且新数据也有1000个节点时才考虑跳过
          if (currentNodeCount >= 1000 && newNodeCount >= 1000 && 
              oldVal && newVal &&
              JSON.stringify(oldVal.nodes?.slice(0, 3)) === JSON.stringify(newVal.nodes?.slice(0, 3))) {
            console.log('【动效优化】已达到1000节点且数据相同，跳过更新')
            return
          }
        }
        
        if (newVal.nodes && newVal.nodes.length > 0) {
          // Temporarily added for testing
          const testNode = newVal.nodes.find(node => node.user === '英语前缀')
          if (testNode) {
            console.log('【测试】找到了关键词 "英语前缀" 的节点:', testNode)
          } else {
            console.log('【测试】在全部节点中未找到关键词 "英语前缀"。')
          }
        }
        
        if (Graph && newVal.nodes && newVal.nodes.length > 0) {
          // 使用更长的节流时间避免频繁更新，每800ms最多更新一次
          if (this.updateThrottle) {
            clearTimeout(this.updateThrottle)
          }

          this.updateThrottle = setTimeout(() => {
            console.log('【动效优化】执行数据更新')
            const gData = JSON.parse(JSON.stringify(newVal))
            // 严格限制节点数量为1000
            if (gData.nodes.length > 1000) {
              console.log(`【节点限制】原始节点数: ${gData.nodes.length}, 限制为1000`)
              gData.nodes = gData.nodes.slice(0, 1000)
              if (gData.links) {
                const getNodeId = (node) => node.id || node.bookId || node.nodeId
                const nodeIds = new Set(gData.nodes.map(n => getNodeId(n)))
                gData.links = gData.links.filter(l => nodeIds.has(l.source) && nodeIds.has(l.target))
              }
            }
            
            // 确保当前图形中的节点数量不超过1000
            const currentGraphData = Graph.graphData()
            if (currentGraphData.nodes && currentGraphData.nodes.length > 1000) {
              console.log(`【节点限制】当前图形节点数: ${currentGraphData.nodes.length}, 强制限制为1000`)
              gData.nodes = gData.nodes.slice(0, 1000)
              if (gData.links) {
                const getNodeId = (node) => node.id || node.bookId || node.nodeId
                const nodeIds = new Set(gData.nodes.map(n => getNodeId(n)))
                gData.links = gData.links.filter(l => nodeIds.has(l.source) && nodeIds.has(l.target))
              }
            }
            
            Graph.graphData(gData)

            // 在数据更新后重新应用连接关系
            if (this.keywordsConnections.length > 0) {
              setTimeout(() => {
                this.updateGraphConnections()
              }, 100)
            } else {
              // 如果还没有获取过连接数据，则获取
              this.fetchKeywordsConnections()
            }
          }, 800) // 增加节流时间到800ms
        }
      },
      deep: true,
      immediate: true // 改回 true，确保能处理初始数据
    }
  },
  components: {
    book,
    // MindMap,
    BookDetailsPopup,
    BookDetail
  },
  methods: {
    ...mapMutations({
      setChosenBookId: 'book/SET_CHOSED_BOOK_ID',
      setShowDetails: 'book/showDetails'
    }),
    emitCameraDistance() {
      if (Graph && Graph.camera) {
        const distance = Graph.camera().position.length()
        this.$emit('zoom-changed', distance)
      }
    },
    handleClick () {
      this.currentBookId = null
      this.currentNodeId = null
      this.currentBondList = [] // 清空星链节点列表
      this.lineTitle = null // 清空标题
      this.isShowInfo = false // 隐藏信息显示
    },
    // 处理添加关联词条
    handleAddRelatedKeyword (newKeyword) {
      // 检查是否已存在（通过关键词名称去重）
      const exists = this.currentBondList.some(
        item => item.keywords === newKeyword.keywords
      )
      
      if (!exists) {
        // 创建唯一标识
        const uniqueKey = `${newKeyword.book_keywords_id || 'null'}_${newKeyword.member_keywords_id || 'null'}_${newKeyword.keywords}`
        
        // 添加到关联节点列表
        this.currentBondList.push({
          keywords: newKeyword.keywords,
          book_keywords_id: newKeyword.book_keywords_id,
          member_keywords_id: newKeyword.member_keywords_id,
          uniqueKey: uniqueKey
        })
        
        console.log('【添加关联词条】新词条已添加到关联节点列表:', newKeyword.keywords)
      } else {
        console.log('【添加关联词条】词条已存在，跳过:', newKeyword.keywords)
      }
    },
    // 获取关键词之间的连接数据
    fetchKeywordsConnections() {
      request('/api/Keywords/getStarList').then(result => {
        // 检查新接口返回的数据格式
        if (result && result.lineData && result.pointData && Array.isArray(result.lineData) && Array.isArray(result.pointData)) {
          // 创建关键词到节点信息的映射
          const keywordToNodeMap = {};
          result.pointData.forEach(point => {
            keywordToNodeMap[point.keywords] = {
              book_keywords_id: point.book_keywords_id,
              member_keywords_id: point.member_keywords_id,
              keywords: point.keywords
            };
          });
          
          // 将新数据格式转换为原有格式
          const convertedConnections = [];
          result.lineData.forEach(line => {
            const frontNode = keywordToNodeMap[line.keywords_front];
            const behindNode = keywordToNodeMap[line.keywords_behind];
            
            // 只有当前后节点都能找到对应信息时才添加连接
            if (frontNode && behindNode) {
              convertedConnections.push({
                keywords_front: frontNode,
                keywords_behind: behindNode
              });
            }
          });
          
          this.keywordsConnections = convertedConnections;
          
          // 延迟执行，确保节点已完全加载
          setTimeout(() => {
            this.updateGraphConnections();
          }, 500);
        } else {
          console.error('获取关键词连接失败，无效的数据格式:', result);
        }
      }).catch(err => {
        console.error('获取关键词连接出错:', err);
      });
    },
    // 更新图形中的连接关系
    updateGraphConnections() {
      if (!Graph || !this.keywordsConnections.length) return;
      
      try {
        // 性能优化：限制处理的连接数量
        const maxConnections = 500; // 最多处理500个连接
        const connections = this.keywordsConnections.length > maxConnections 
          ? this.keywordsConnections.slice(0, maxConnections) 
          : this.keywordsConnections;
        
        const graphData = Graph.graphData();
        const nodes = [...graphData.nodes];
        const links = [...graphData.links];
        
        // 创建节点ID到节点对象的映射，提高查找效率
        const nodeMap = {};
        nodes.forEach(node => {
          if (node.bookId) {
            nodeMap[node.bookId] = node;
          }
        });
        
        // 简化方法：直接为相关联的节点对设置相同颜色
        
        // 预定义一些鲜艳的颜色
        const colors = [
          '#FF5733', // 橙红色
          '#33FF57', // 亮绿色
          '#3357FF', // 蓝色
          '#FF33A8', // 粉红色
          '#33FFF5', // 青色
          '#F5FF33', // 黄色
          '#FF33F5', // 紫红色
          '#33A8FF', // 天蓝色
          '#A833FF', // 紫色
          '#FF8C33'  // 橙色
        ];
        
        // 创建直接的节点对映射
        const nodePairs = [];
        
        // 收集所有相关联的节点对
        connections.forEach(conn => {
          const sourceId = conn.keywords_front?.book_keywords_id;
          const targetId = conn.keywords_behind?.book_keywords_id;
          
          if (sourceId && targetId) {
            // 使用映射快速查找节点，避免每次都遍历整个数组
            const sourceNode = nodeMap[sourceId];
            const targetNode = nodeMap[targetId];
            
            if (sourceNode && targetNode) {
              // 记录这对相关联的节点
              nodePairs.push({
                source: sourceNode,
                target: targetNode,
                keywords: [conn.keywords_front?.keywords, conn.keywords_behind?.keywords]
              });
            }
          }
        });
        
        // 为相关联的节点分组
        const nodeGroups = {};
        const nodeToGroup = {};
        
        // 首先为每个节点创建一个单独的组
        Object.keys(nodeMap).forEach(bookId => {
          const node = nodeMap[bookId];
          nodeToGroup[bookId] = bookId;
          nodeGroups[bookId] = [node];
        });
        
        // 合并相关联的节点组 - 使用并查集算法的简化版本
        nodePairs.forEach(pair => {
          const sourceId = pair.source.bookId;
          const targetId = pair.target.bookId;
          
          if (sourceId && targetId) {
            const sourceGroup = nodeToGroup[sourceId];
            const targetGroup = nodeToGroup[targetId];
            
            // 如果两个节点在不同组中，合并它们
            if (sourceGroup && targetGroup && sourceGroup !== targetGroup) {
              // 创建新组ID（使用较小的ID）
              const newGroupId = sourceGroup < targetGroup ? sourceGroup : targetGroup;
              const oldGroupId = sourceGroup < targetGroup ? targetGroup : sourceGroup;
              
              // 更新组映射
              nodeGroups[sourceGroup].forEach(node => {
                if (node.bookId) {
                  nodeToGroup[node.bookId] = newGroupId;
                }
              });
              
              nodeGroups[targetGroup].forEach(node => {
                if (node.bookId) {
                  nodeToGroup[node.bookId] = newGroupId;
                }
              });
              
              // 合并节点组
              nodeGroups[newGroupId] = [...nodeGroups[sourceGroup], ...nodeGroups[targetGroup]];
              
              // 删除旧组
              if (newGroupId !== oldGroupId) {
                delete nodeGroups[oldGroupId];
              }
            }
          }
        });
        
        // 为每个组分配颜色
        let colorIndex = 0;
        const processedGroups = new Set();
        
        // 性能优化：只处理有多个节点的组，且限制处理的组数量
        const maxGroups = 20; // 最多处理20个组
        let groupCount = 0;
        
        Object.entries(nodeGroups).forEach(([groupId, groupNodes]) => {
          // 只处理有多个节点的组，且限制数量
          if (groupNodes.length > 1 && !processedGroups.has(groupId) && groupCount < maxGroups) {
            const color = colors[colorIndex % colors.length];
            colorIndex++;
            groupCount++;
            
            // 为组内所有节点设置相同的颜色
            groupNodes.forEach(node => {
              node.color = color;
            });
            
            processedGroups.add(groupId);
          }
        });
        
        // 创建新的连接用于力导向布局
        const newLinks = [];
        const maxLinks = 300; // 最多添加300个连接
        let linkCount = 0;
        
        for (const pair of nodePairs) {
          if (linkCount >= maxLinks) break;
          
          // 检查这个连接是否已经存在
          const linkExists = links.some(link => {
            const srcId = typeof link.source === 'object' ? 
              (link.source.bookId || link.source.id) : link.source;
            const tgtId = typeof link.target === 'object' ? 
              (link.target.bookId || link.target.id) : link.target;
            
            return (srcId === pair.source.bookId && tgtId === pair.target.bookId) || 
                   (srcId === pair.target.bookId && tgtId === pair.source.bookId);
          });
          
          if (!linkExists) {
            newLinks.push({
              source: pair.source,
              target: pair.target,
              // 添加连线属性
              value: 1,
              width: 1,
              color: '#ffffff',
              opacity: 0.5
            });
            linkCount++;
          }
        }
        
        if (newLinks.length > 0) {
          // 更新图形数据，使颜色变化生效
          Graph.nodeColor(node => node.color);
          // 确保所有节点大小一致
          Graph.nodeVal(1);
          
          // 直接更新整个图形数据，确保连线被添加
          const updatedData = {
            nodes: nodes,
            links: [...links, ...newLinks]
          };
          
          // 使用新的数据更新图形
          Graph.graphData(updatedData);
          
          // 设置连线样式
          Graph.linkWidth(1)
            .linkColor(() => '#ffffff')
            .linkOpacity(0.5)
            .linkDirectionalParticles(2);
          
          // 更新力导向参数，让相关联的节点相互吸引
          const linkForce = Graph.d3Force('link');
          if (linkForce) {
            linkForce
              .distance(50) // 统一设置距离
              .strength(0.5); // 统一设置连接强度
          }
          
          // 重新启动力导向模拟
          Graph.d3ReheatSimulation();
        }
      } catch (error) {
        console.error('更新图形连接时出错:', error);
      }
    },
    nodeAnimation (node) {
      this.activeLabel.text = ''
      bloomPass.strength = 0
      const distance = 200
      const distRatio = 1 + distance / Math.hypot(node.x, node.y, node.z)
      const newPos = node.x || node.y || node.z
        ? { x: node.x * distRatio, y: node.y * distRatio, z: node.z * distRatio }
        : { x: 0, y: 0, z: distance } // special case if node is in (0,0,0)
      
      // 修改：始终保持lookAt为原点(0,0,0)，确保星云中心不偏移
      Graph.cameraPosition(
        newPos, // new position
        { x: 0, y: 0, z: 0 }, // 始终lookAt原点，保持星云中心在屏幕中心
        3000 // ms transition duration
      )
      setTimeout(() => {
        bloomPass.strength = 2
        this.activeLabel = {
          text: node.user,
          x: window.innerWidth / 2 - (node.user.length * 8),
          y: (window.innerHeight / 2) - 50
        }
      }, 3100)
    },
    init () {
      const elem = document.getElementById('3d-graph')
      const initialData = JSON.parse(JSON.stringify(this.forceData))
      if (initialData.nodes.length > 1000) {
        initialData.nodes = initialData.nodes.slice(0, 1000)
        if (initialData.links) {
          const getNodeId = (node) => node.id || node.bookId || node.nodeId
          const nodeIds = new Set(initialData.nodes.map(n => getNodeId(n)))
          initialData.links = initialData.links.filter(l => nodeIds.has(l.source) && nodeIds.has(l.target))
        }
      }
      Graph = ForceGraph3D({
        antialias: true,
        // 启用WebGL性能优化
        rendererConfig: {
          powerPreference: 'high-performance',
          antialias: true
        }
      })(elem)
        .graphData(initialData)
        .backgroundColor('#000')
        .nodeAutoColorBy('group')
        .nodeResolution(8) // 降低节点分辨率以提高性能
        .nodeRelSize(5) // 节点相对大小
        .warmupTicks(20) // 减少预热迭代次数以提高性能
        .cooldownTicks(20) // 减少冷却迭代次数以提高性能
        .cooldownTime(2000) // 减少冷却时间以提高性能
        .d3AlphaDecay(0.05) // 增加alpha衰减率，使力导向算法更快收敛
        .d3VelocityDecay(0.3) // 增加速度衰减率，减少计算量
        .nodeColor(node => node.color || null) // 使用设置的颜色，如果没有则自动分配
        .linkWidth(1) // 设置连线宽度
        .linkColor(() => '#ffffff') // 设置连线颜色为白色
        .linkOpacity(0.5) // 设置连线透明度
        .linkDirectionalParticles(2) // 添加方向粒子
        .linkDirectionalParticleWidth(2) // 设置方向粒子宽度
        .enableNodeDrag(false) // 禁用节点拖拽以提高性能
        .enableNavigationControls(true) // 保持导航控制
        .showNavInfo(false) // 隐藏导航信息
        .nodeLabel(node => {
          return `<div class="node-label">
              <p>${node.user}</p>
            <div>`
        })
        .linkWidth(link => link.lines_code === 3 ? 2 : 1) // 设置连接线宽度
        .linkColor(link => {
          // 根据lines_code设置不同的连接线颜色
          if (link.lines_code === 3) {
            return '#ffffff'; // 虚线用白色
          } else if (link.lines_code === 1) {
            return '#4488ff'; // 类型1用蓝色
          }
          return '#aaaaaa'; // 默认灰色
        })
        .linkMaterial(link => {
          // 根据lines_code设置不同的连接线样式
          if (link.lines_code === 3) {
            return dashedMaterial;
          } else if (link.lines_code === 1) {
            return new THREE.LineBasicMaterial({
              color: '#4488ff',
              linewidth: 1
            });
          }
          return new THREE.LineBasicMaterial({
            color: '#ffffff',
            linewidth: 1
          });
        })
        .linkDirectionalParticles(link => link.lines_code === 3 ? 4 : 0) // 为虚线添加粒子效果
        .linkDirectionalParticleWidth(2)
        .linkPositionUpdate(lineObj => { 
          if (lineObj.computeLineDistances) {
            lineObj.computeLineDistances();
          }
          return true;
        })
        .linkDirectionalArrowRelPos(1)
        .linkCurvature(0) // 线的弯曲程度
        .linkDirectionalArrowLength(link => {
          return link.size || 2;
        }) // 箭头大小
        .onNodeClick(node => {
          // 更新当前点击的节点信息，以触发 book 组件的数据加载
          this.currentBookId = node.bookId;
          this.currentNodeId = node.nodeId;
          this.currentKeyword = node.user;
          
          if (node.titleId) {
            this.lineTitle = {
              title: node.title,
              id: node.titleId
            }
          } else {
            this.lineTitle = null
          }
          bloomPass.strength = 0
          
          // 1. 检查是否为星链节点（通过检查图形中是否有实际的连线）
          console.log('【调试】点击节点:', node.user)
          
          // 获取当前图形中的连线数据
          const currentGraphData = Graph.graphData();
          const currentLinks = currentGraphData.links;
          const currentNodes = currentGraphData.nodes;
          
          // 检查当前节点是否在图形中有实际的连线
          const hasVisibleConnections = currentLinks.some(link => {
            const sourceId = typeof link.source === 'object' ? 
              (link.source.bookId || link.source.id) : link.source;
            const targetId = typeof link.target === 'object' ? 
              (link.target.bookId || link.target.id) : link.target;
            
            return (sourceId === node.bookId || targetId === node.bookId || 
                    sourceId === node.id || targetId === node.id);
          });
          
          console.log('【调试】节点在图形中是否有可见连线:', hasVisibleConnections)
          
          if (hasVisibleConnections) {
            // 如果有可见连线，说明是星链节点，获取所有连接的节点
            const connectedNodeIds = new Set();
            
            currentLinks.forEach(link => {
              const sourceId = typeof link.source === 'object' ? 
                (link.source.bookId || link.source.id) : link.source;
              const targetId = typeof link.target === 'object' ? 
                (link.target.bookId || link.target.id) : link.target;
              
              if (sourceId === node.bookId || sourceId === node.id) {
                connectedNodeIds.add(targetId);
              }
              if (targetId === node.bookId || targetId === node.id) {
                connectedNodeIds.add(sourceId);
              }
            });
            
            // 根据连接的节点ID找到对应的节点信息，并确保严格去重
            const relatedNodes = [];
            const addedNodeKeys = new Set(); // 用于去重
            const addedKeywords = new Set(); // 用关键词去重
            
            currentNodes.forEach(graphNode => {
              const nodeId = graphNode.bookId || graphNode.id;
              const keyword = graphNode.user;
              
              // 创建更严格的唯一标识
              const uniqueKey = `${graphNode.bookId || 'null'}_${graphNode.nodeId || 'null'}_${keyword}`;
              
              if (connectedNodeIds.has(nodeId) && 
                  nodeId !== node.bookId && 
                  nodeId !== node.id && 
                  !addedNodeKeys.has(uniqueKey) &&
                  !addedKeywords.has(keyword)) {
                
                addedNodeKeys.add(uniqueKey);
                addedKeywords.add(keyword);
                
                relatedNodes.push({
                  book_keywords_id: graphNode.bookId,
                  member_keywords_id: graphNode.nodeId,
                  keywords: keyword,
                  uniqueKey: uniqueKey // 添加唯一标识用于调试
                });
              }
            });
            
            console.log('【调试】去重前连接节点数:', connectedNodeIds.size, '去重后关联节点数:', relatedNodes.length);
            console.log('【调试】关联节点关键词:', relatedNodes.map(n => n.keywords));
            
            this.currentBondList = relatedNodes;
            console.log('【调试】星链节点，关联节点数量:', this.currentBondList.length)
          } else {
            // 如果没有可见连线，说明是单独节点
            this.currentBondList = []
            console.log('【调试】单独节点，无关联节点')
          }
          
          // 2. 不再自动获取通过keywordsConnections的关联节点，避免显示过多无关信息
          // 注释掉这部分逻辑，只保留星链的关联显示
          
          this.nodeAnimation(node)
          this.isShowInfo = true
        })

      // 添加镜头变化事件监听
      Graph.controls().addEventListener('change', this.emitCameraDistance)
      
      // 延迟设置控制器，确保完全初始化后再配置
      setTimeout(() => {
        const controls = Graph.controls()
        controls.enablePan = false
        controls.enableKeys = false
        
        // 锁定相机目标点为原点，确保星云中心始终在屏幕中心
        controls.target.set(0, 0, 0)
        
        // 监听控制器变化，强制保持目标点为原点
        controls.addEventListener('change', () => {
          if (controls.target.x !== 0 || controls.target.y !== 0 || controls.target.z !== 0) {
            controls.target.set(0, 0, 0)
          }
        })
        
        // 直接禁用右键事件
        const domElement = Graph.renderer().domElement
        domElement.addEventListener('contextmenu', (e) => {
          e.preventDefault()
          return false
        })
        
        // 监听鼠标事件，阻止右键拖拽
        domElement.addEventListener('mousedown', (e) => {
          if (e.button === 2) { // 右键
            e.preventDefault()
            e.stopPropagation()
            return false
          }
        })
        
        domElement.addEventListener('mousemove', (e) => {
          if (e.buttons === 2) { // 右键按下状态
            e.preventDefault()
            e.stopPropagation()
            return false
          }
        })
      }, 100)
      // 强行设置渲染器背景为透明
      Graph.renderer().setClearColor(0x000000, 0.1);

      // 动画
      let angle = 0
      const distance = 1400
      setInterval(() => {
        if (isRotationActive) {
          Graph.cameraPosition({
            x: distance * Math.sin(angle),
            z: distance * Math.cos(angle)
          })
          angle += Math.PI / 300
        }
      }, 40)
      setTimeout(() => {
        isRotationActive = false
      }, 5000)

      // 在初始动画结束后，模拟鼠标滚轮缩小效果
      setTimeout(() => {
        // 获取当前相机位置
        const camera = Graph.camera();
        if (!camera) return;
        const currentPos = camera.position;

        // 计算当前到原点的距离
        const currentDistance = Math.hypot(currentPos.x, currentPos.y, currentPos.z);
        
        // 如果距离已经很远，则不再缩小
        if (currentDistance > 16000) return;

        // 设定新的目标距离，经过计算以使最终视觉半径接近200px
        const newDistance = 18000;
        // 计算缩放比例
        const scale = newDistance / currentDistance;

        // 计算新的相机位置
        const newPos = {
          x: currentPos.x * scale,
          y: currentPos.y * scale,
          z: currentPos.z * scale
        };
        
        // 使用2秒的动画平滑过渡到新位置，实现缩小效果
        Graph.cameraPosition(newPos, { x: 0, y: 0, z: 0 }, 2000);
      }, 5100); // 在旋转停止后(5000ms)立即开始

      bloomPass = new UnrealBloomPass()
      bloomPass.strength = 0.5 // 发光强度
      //bloomPass.radius = 1 // 表示泛光散发的半径
      //bloomPass.threshold = 0 // 表示产生泛光的强度阀值，如果照在屋里上的光照强度大于该值就会产生泛光
      Graph.postProcessingComposer().addPass(bloomPass)
      
      // 标记图形已初始化，避免 forceData watcher 重复处理初始数据
      this.isGraphInitialized = true
      console.log('【动效优化】图形初始化完成，启用数据监听')
      
      this.fetchKeywordsConnections() // 在图形初始化完成后调用fetchKeywordsConnections
    },
    // 清除页面上的截面标签
    clearObjectsLabels () {
      // 移除之前所有的标识符元素
      // isShowInfo = false;
      const visibleMarkerList =
        document.getElementsByClassName('visible-marker')
      while (visibleMarkerList.length > 0) {
        visibleMarkerList[0].parentNode.removeChild(visibleMarkerList[0])
      }
    },
    clearShow () {
      this.isShowInfo = false
      this.isShowInfoArrayLocked = false
      this.isShowInfoArray = false
    },
    showBookDetail (id) {
      if (id) {
        this.setChosenBookId(id)
        const previousLabel = document.getElementById('object-label')
        if (previousLabel) {
          previousLabel.remove()
        }
        this.keyword = ''
        this.clearObjectsLabels()
        this.clearShow()
      } else {
        this.$message({
          message: '暂无对应图书',
          type: 'error',
          duration: 1000
        })
      }
    },
    // z-index 管理方法 - 递增策略
    bringBookContainerToFront(event) {
      console.log('【调试】bringBookContainerToFront 被触发', event)
      // 递增当前最大z-index值
      this.currentMaxZIndex++
      // 将星点/星链信息置于最顶层
      this.bookContainerZIndex = this.currentMaxZIndex
      console.log('【调试】星点信息置于顶层，当前z-index:', this.bookContainerZIndex)
    },
    bringBookDetailToFront(eventType) {
      console.log('【调试】bringBookDetailToFront 被触发，事件类型:', eventType)
      // 递增当前最大z-index值
      this.currentMaxZIndex++
      // 将书籍详情置于最顶层
      this.bookDetailZIndex = this.currentMaxZIndex
      console.log('【调试】书籍详情置于顶层，当前z-index:', this.bookDetailZIndex)
    },
    bringBookDetailsPopupToFront(eventType) {
      console.log('【调试】bringBookDetailsPopupToFront 被触发，事件类型:', eventType)
      // 递增当前最大z-index值
      this.currentMaxZIndex++
      // 将书籍详情弹窗置于最顶层
      this.bookDetailsPopupZIndex = this.currentMaxZIndex
      console.log('【调试】书籍详情弹窗置于顶层，当前z-index:', this.bookDetailsPopupZIndex)
    },
    // 切换信息框收起/展开状态
    toggleBookContainer() {
      this.isBookContainerCollapsed = !this.isBookContainerCollapsed
    }
  },
  mounted () {
    this.init()
    const container = document.getElementById('book-container')
    container.addEventListener('mousedown', function (event) {
      isDragging = true
      startX = event.clientX
      scrollLeft = container.scrollLeft
    })
    container.addEventListener('mousemove', function (event) {
      if (!isDragging) return

      var distance = event.clientX - startX
      container.scrollLeft = scrollLeft - distance
    })

    container.addEventListener('mouseup', function (event) {
      isDragging = false
    })

    EventBus.$on('openDetail', (item) => {
      this.isShowInfo = true
      const { nodes } = Graph.graphData()
      let node = null

      const findNodeFromData = (data, criteria) => {
        return data.find(i => {
          if (criteria.book_keywords_id || criteria.bookId) {
            return i.bookId === (criteria.book_keywords_id || criteria.bookId)
          } else {
            return i.nodeId === (criteria.member_keywords_id || criteria.nodeId)
          }
        })
      }

      let searchCriteria = item
      const isKeywordTitle = item.type && item.type === 'KEYWORD_TITLE'

      if (isKeywordTitle) {
        const currentBond = getBondTitle(this.bondList, item)
        if (!currentBond || !currentBond.point_list || currentBond.point_list.length === 0) {
          this.$message({ message: '关联数据不完整', type: 'error' })
          return
        }
        const first = currentBond.point_list[0]
        item.user = first.keywords
        this.currentBondList = currentBond.point_list.filter(child => child.keywords !== item.user)
        searchCriteria = first
        this.lineTitle = {
          title: currentBond.title[0].lines_title,
          id: currentBond.title[0].mix_keywords_lines_id
        }
      }

      node = findNodeFromData(nodes, searchCriteria)

      if (!node) {
        const fullNodes = this.forceData.nodes
        const targetNodeData = findNodeFromData(fullNodes, searchCriteria)

        if (targetNodeData) {
          const currentGraphData = Graph.graphData()
          const newNodes = [...currentGraphData.nodes]
          
          // 搜索结果节点无视1000节点限制，直接添加
          // 但如果节点数量过多，移除一些旧节点以保持性能
          if (newNodes.length >= 1200) {
            // 当节点数超过1200时，移除一些旧节点，但保留搜索结果
            const nodesToRemove = newNodes.length - 1000 + 1; // +1 是为了给新节点留位置
            for (let i = 0; i < nodesToRemove; i++) {
              newNodes.shift();
            }
            console.log(`【搜索节点】移除了${nodesToRemove}个旧节点，为搜索结果让位`)
          }
          
          newNodes.push(targetNodeData)
          console.log(`【搜索节点】强制添加搜索结果节点，当前节点数: ${newNodes.length}`)

          // 简化方案：直接添加节点，保持所有现有连接
          const getNodeId = (node) => node.id || node.bookId || node.nodeId
          const updatedNodes = [...newNodes, targetNodeData]
          const nodeIds = new Set(updatedNodes.map(n => getNodeId(n)))
          
          // 保持当前图形中的所有连接（包括星链连接）
          const currentLinks = currentGraphData.links || []
          
          // 添加新节点相关的原始连接
          const originalLinks = this.forceData.links || []
          const newNodeLinks = originalLinks.filter(l => {
            const targetNodeId = getNodeId(targetNodeData)
            return (l.source === targetNodeId || l.target === targetNodeId) && 
                   nodeIds.has(l.source) && nodeIds.has(l.target)
          })
          
          // 合并所有连接，避免重复
          const allLinksMap = new Map()
          
          // 首先保持所有现有连接（包括星链连接）
          currentLinks.forEach(link => {
            const sourceId = typeof link.source === 'object' ? 
              (link.source.bookId || link.source.id) : link.source
            const targetId = typeof link.target === 'object' ? 
              (link.target.bookId || link.target.id) : link.target
            const key = `${sourceId}-${targetId}`
            allLinksMap.set(key, link)
          })
          
          // 然后添加新节点的连接
          newNodeLinks.forEach(link => {
            const key = `${link.source}-${link.target}`
            const reverseKey = `${link.target}-${link.source}`
            if (!allLinksMap.has(key) && !allLinksMap.has(reverseKey)) {
              allLinksMap.set(key, link)
            }
          })
          
          const finalLinks = Array.from(allLinksMap.values())
          
          console.log(`【搜索节点】保持连接：现有${currentLinks.length}个，新增${newNodeLinks.length}个，最终${finalLinks.length}个`)
          
          // 更新图形数据
          Graph.graphData({ nodes: updatedNodes, links: finalLinks })
          Graph.d3ReheatSimulation()

          // 重新应用星链连接，确保包含新节点
          setTimeout(() => {
            if (this.keywordsConnections.length > 0) {
              console.log('【搜索节点】重新应用星链连接，包含新节点')
              this.updateGraphConnections()
            }
          }, 300)

          setTimeout(() => {
            const foundNode = findNodeFromData(Graph.graphData().nodes, searchCriteria)
            if (foundNode) {
              if (!isKeywordTitle) {
                const currentBond = getBondNode(this.bondList, foundNode)
                if (currentBond) {
                  this.currentBondList = currentBond.point_list.filter(child => child.keywords !== item.user)
                } else {
                  this.currentBondList = []
                }
                if (foundNode.titleId) {
                  this.lineTitle = {
                    title: foundNode.title,
                    id: foundNode.titleId
                  }
                } else {
                  this.lineTitle = null
                }
              }
              this.currentKeyword = item.user
              this.currentBookId = foundNode.bookId
              this.currentNodeId = foundNode.nodeId
              this.currentKeyword = foundNode.user
              this.nodeAnimation(foundNode)
            } else {
              this.$message({ message: '添加节点后仍未找到', type: 'error' })
            }
          }, 100)
          return
        }
      }

      if (!node) {
        this.$message({ message: '未找到相关节点', type: 'error' })
        return
      }

      if (!isKeywordTitle) {
        const currentBond = getBondNode(this.bondList, node)
        if (currentBond) {
          this.currentBondList = currentBond.point_list.filter(child => child.keywords !== item.user)
        } else {
          this.currentBondList = []
        }
        if (node.titleId) {
          this.lineTitle = {
            title: node.title,
            id: node.titleId
          }
        } else {
          this.lineTitle = null
        }
      }

      this.currentKeyword = item.user
      this.currentBookId = node.bookId
      this.currentNodeId = node.nodeId
      this.currentKeyword = node.user
      this.nodeAnimation(node)
    })
  },
  
  beforeDestroy () {
    // 清理定时器
    if (this.updateThrottle) {
      clearTimeout(this.updateThrottle)
    }
    // 移除事件监听
    if (Graph && Graph.controls) {
      Graph.controls().removeEventListener('change', this.emitCameraDistance)
    }
  }
}

</script>

<style lang="scss">
.book-container{
   position: absolute;
   bottom: 15px;
    /* z-index: 9999999; */
    bottom: 0;
    max-width: 100%;
    padding-top: 30px;
    background-color: rgba(0, 0, 0, 0.7);
    border-radius: 8px 8px 0 0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.3s ease;
    
    // 收起按钮样式
    .collapse-toggle {
      position: absolute;
      top: -30px;
      right: 20px;
      background-color: rgba(0, 0, 0, 0.8);
      color: #fff;
      padding: 6px 12px;
      border-radius: 6px 6px 0 0;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-bottom: none;
      cursor: pointer;
      font-size: 12px;
      display: flex;
      align-items: center;
      gap: 4px;
      transition: all 0.2s ease;
      user-select: none;
      
      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.4);
      }
      
      i {
        font-size: 14px;
      }
    }
    
    // 内容区域样式
    .book-content {
      display: flex;
      overflow-x: scroll;
      transition: all 0.3s ease;
      
      .keyword-title{
        position: absolute;
        color: #fff;
        top: 0;
        left: 12px;
        z-index: 9;
      }
      .related-nodes-count {
        position: absolute;
        color: #8faadc;
        top: 0;
        right: 12px;
        z-index: 9;
        font-size: 14px;
        background-color: rgba(0, 0, 0, 0.3);
        padding: 2px 8px;
        border-radius: 4px;
        border: 1px solid rgba(143, 170, 220, 0.3);
      }
    }
    
    // 收起状态样式
    &.collapsed {
      .book-content {
        max-height: 0;
        overflow: hidden;
        opacity: 0;
      }
      
      .collapse-toggle {
        background-color: rgba(0, 0, 0, 0.9);
      }
    }
}

// 添加滚动条样式，使其更美观
.book-container::-webkit-scrollbar {
  height: 6px;
}

.book-container::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.book-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.book-container::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

// 添加滚动提示
.book-container::after {
  content: "";
  position: absolute;
  right: 0;
  bottom: 50%;
  width: 40px;
  height: 40px;
  background: linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(34, 47, 54, 0.8) 100%);
  pointer-events: none;
  opacity: 0.7;
}

.node-label{
  color: rgb(234, 178, 4);
  background-color: black;
  border: 1px solid rgb(154, 182, 200);
  padding: 2px 4px;
}
.node-active{
  position: absolute;
  display: inline-block;
  padding: 2px 4px;
  color: rgb(234, 178, 4);
  border: 1px solid rgb(154, 182, 200);
}
</style>
